import { useState, useRef, useEffect } from 'react';
import { TextField, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';

export default function OTPInput({ value = '', onChange, length = 6 }) {
  const [otp, setOtp] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    // Sincroniza o valor externo com o estado interno
    const newOtp = Array(length).fill('');
    if (value) {
      const valueArray = value.split('').slice(0, length);
      valueArray.forEach((char, index) => {
        if (index < length) {
          newOtp[index] = char.toUpperCase();
        }
      });
    }
    setOtp(newOtp);
  }, [value, length]);

  const handleChange = (index, newValue) => {
    // Remove caracteres não alfanuméricos e converte para maiúsculo
    const cleanedValue = newValue.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    if (cleanedValue.length > 1) {
      // Se colou múltiplos caracteres, distribui pelos campos
      const chars = cleanedValue.split('').slice(0, length - index);
      const newOtp = [...otp];
      
      chars.forEach((char, i) => {
        if (index + i < length) {
          newOtp[index + i] = char.toUpperCase();
        }
      });
      
      setOtp(newOtp);
      const otpString = newOtp.join('');
      onChange(otpString);
      
      // Foca no próximo campo vazio ou no último
      const nextIndex = Math.min(index + chars.length, length - 1);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = cleanedValue;
    setOtp(newOtp);
    
    const otpString = newOtp.join('');
    onChange(otpString);

    // Auto-avanço para o próximo campo
    if (cleanedValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace: apaga e volta para o campo anterior
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newOtp = [...otp];
      newOtp[index - 1] = '';
      setOtp(newOtp);
      const otpString = newOtp.join('');
      onChange(otpString);
    }
    // Delete: apaga o campo atual
    else if (e.key === 'Delete') {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      const otpString = newOtp.join('');
      onChange(otpString);
    }
    // Arrow keys: navega entre campos
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const cleanedData = pastedData.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, length);
    
    const newOtp = Array(length).fill('');
    cleanedData.split('').forEach((char, index) => {
      if (index < length) {
        newOtp[index] = char;
      }
    });
    
    setOtp(newOtp);
    const otpString = newOtp.join('');
    onChange(otpString);
    
    // Foca no último campo preenchido ou no último campo
    const lastFilledIndex = Math.min(cleanedData.length - 1, length - 1);
    if (inputRefs.current[lastFilledIndex]) {
      inputRefs.current[lastFilledIndex].focus();
    }
  };

  return (
    <Stack 
      direction="row" 
      spacing={{ xs: 1, sm: 1.5, md: 2 }} 
      justifyContent="center" 
      alignItems="center"
      sx={{ 
        flexWrap: 'wrap',
        px: { xs: 1, sm: 0 }
      }}
    >
      {otp.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => { inputRefs.current[index] = el; }}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          inputProps={{
            maxLength: 1,
            style: {
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: 600,
              textTransform: 'uppercase',
            },
          }}
          sx={{
            width: { xs: 44, sm: 50, md: 56 },
            height: { xs: 44, sm: 50, md: 56 },
            '& .MuiOutlinedInput-root': {
              height: { xs: 44, sm: 50, md: 56 },
              '& fieldset': {
                borderWidth: 2,
                borderColor: alpha('#637381', 0.32),
              },
              '&:hover fieldset': {
                borderColor: alpha('#2065D1', 0.5),
              },
              '&.Mui-focused fieldset': {
                borderColor: '#2065D1',
                borderWidth: 2,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: { xs: '12px', sm: '14px', md: '16px' },
              fontSize: { xs: '18px', sm: '20px', md: '24px' },
            },
          }}
          variant="outlined"
          autoComplete="off"
        />
      ))}
    </Stack>
  );
}

